import { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LogoLockup } from "./Logo";
import { scrollToId } from "../../lib/scroll";

const LINKS = [
    { label: "Vision", hash: "#vision" },
    { label: "First World", hash: "#world" },
    { label: "Different", hash: "#different" },
    { label: "The Slice", hash: "#slice" },
    { label: "Roadmap", hash: "#roadmap" },
    { label: "Community", hash: "#community" },
];

export const Nav = () => {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const go = (hash) => {
        setOpen(false);
        if (location.pathname !== "/") {
            navigate("/");
            setTimeout(() => scrollToId(hash), 350);
        } else {
            scrollToId(hash);
        }
    };

    return (
        <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
                <button
                    data-testid="nav-logo"
                    onClick={() => go("#top")}
                    className="hover:opacity-80 transition-opacity"
                    aria-label="WRLDKIND Studios home"
                >
                    <LogoLockup />
                </button>

                <nav className="hidden lg:flex items-center gap-8">
                    {LINKS.map((l) => (
                        <button
                            key={l.hash}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={() => go(l.hash)}
                            className="font-mono-meta text-[11px] tracking-[0.25em] uppercase text-slate-400 hover:text-white transition-colors duration-300"
                        >
                            {l.label}
                        </button>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <Link
                        to="/kickstarter"
                        data-testid="nav-kickstarter-button"
                        className="hidden sm:inline-flex items-center gap-2 font-mono-meta text-[11px] tracking-[0.25em] uppercase text-white border border-[#A855F7]/50 hover:border-[#A855F7] hover:bg-[#A855F7]/10 transition-colors duration-300 px-5 py-2.5"
                    >
                        Kickstarter
                        <ArrowUpRight className="w-3.5 h-3.5 text-[#A855F7]" />
                    </Link>
                    <button
                        data-testid="nav-menu-toggle"
                        onClick={() => setOpen(!open)}
                        className="lg:hidden text-white p-2"
                        aria-label="Toggle menu"
                    >
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:hidden overflow-hidden border-t border-white/10 bg-black/90"
                    >
                        <div className="px-5 py-6 flex flex-col gap-5">
                            {LINKS.map((l) => (
                                <button
                                    key={l.hash}
                                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    onClick={() => go(l.hash)}
                                    className="text-left font-mono-meta text-xs tracking-[0.3em] uppercase text-slate-300 hover:text-[#A855F7] transition-colors"
                                >
                                    {l.label}
                                </button>
                            ))}
                            <Link
                                to="/kickstarter"
                                data-testid="mobile-nav-kickstarter-button"
                                onClick={() => setOpen(false)}
                                className="inline-flex items-center gap-2 font-mono-meta text-xs tracking-[0.3em] uppercase text-[#A855F7]"
                            >
                                Kickstarter <ArrowUpRight className="w-3.5 h-3.5" />
                            </Link>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};
